import React, { useState, useEffect, useRef, useCallback } from 'react';
import { formatPhoneInput, isValidPhone } from '../../utils/validation';

const API_BASE = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace(/\/api$/, '')
  : '';

function apiFetch(path, options = {}) {
  return fetch(`${API_BASE}${path}`, { credentials: 'include', ...options })
    .then(response => {
      if (response.status === 403) {
        response.clone().json().then(data => {
          if (data.reason === 'disabled' || data.reason === 'expired') {
            window.dispatchEvent(new CustomEvent('catalog-access-blocked', { detail: data.reason }));
          }
        }).catch(() => {});
      }
      return response;
    });
}

function formatWeight(val) {
  if (!val && val !== 0) return '—';
  return `${parseFloat(val).toFixed(2)} gm`;
}

function formatCurrency(val) {
  if (!val && val !== 0) return '—';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(val);
}

// ── Customer info capture form ────────────────────────────────────────────────
const LOCATION_DEBOUNCE_MS = 500;

function CustomerInfoForm({ onSubmit, loading }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [location, setLocation] = useState(''); // "City, State" — split apart on submit
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [error, setError] = useState('');
  const locationDebounceRef = useRef(null);
  const locationBoxRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (locationBoxRef.current && !locationBoxRef.current.contains(e.target)) {
        setShowLocationDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLocationInput = (value) => {
    setLocation(value);
    if (locationDebounceRef.current) clearTimeout(locationDebounceRef.current);
    if (!value || value.trim().length < 2) {
      setLocationSuggestions([]);
      setShowLocationDropdown(false);
      return;
    }
    locationDebounceRef.current = setTimeout(async () => {
      try {
        const res = await apiFetch(`/api/utils/city-suggest?q=${encodeURIComponent(value)}&country=India`);
        const data = await res.json();
        if (data.success && data.data?.length > 0) {
          setLocationSuggestions(data.data);
          setShowLocationDropdown(true);
        } else {
          setLocationSuggestions([]);
          setShowLocationDropdown(false);
        }
      } catch {
        // Non-fatal — suggestions are a convenience, not required to submit
      }
    }, LOCATION_DEBOUNCE_MS);
  };

  const selectLocation = (loc) => {
    setLocation(`${loc.city}, ${loc.state}`);
    setLocationSuggestions([]);
    setShowLocationDropdown(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) return setError('Please enter your name');
    if (!phone.trim() || !isValidPhone(phone)) {
      return setError('Please enter a valid 10-digit phone number');
    }
    const [city, state] = location.split(',').map(s => s.trim());
    onSubmit({
      customer_name: name.trim(),
      customer_phone: phone.trim(),
      customer_city: city || '',
      customer_state: state || ''
    });
  };

  return (
    <div style={styles.formScreen}>
      <div style={styles.formCard}>
        <div style={styles.brandHeader}>
          <div style={styles.brandTitle}>GDS Jewellers</div>
          <div style={styles.brandSubtitle}>Welcome to our Collection</div>
        </div>

        <div style={styles.formBody}>
          <p style={styles.formIntro}>
            Please share your details to browse our exclusive jewelry collection.
          </p>

          <form onSubmit={handleSubmit} noValidate>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Your Name</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Enter your full name"
                style={styles.input}
                autoComplete="name"
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Phone Number</label>
              <input
                type="tel"
                value={phone}
                onChange={e => setPhone(formatPhoneInput(e.target.value))}
                placeholder="+91 98765 43210"
                style={styles.input}
                autoComplete="tel"
                inputMode="tel"
              />
            </div>

            <div style={{ ...styles.fieldGroup, position: 'relative' }} ref={locationBoxRef}>
              <label style={styles.label}>City / State</label>
              <input
                type="text"
                value={location}
                onChange={e => handleLocationInput(e.target.value)}
                placeholder="Start typing your city…"
                style={styles.input}
                autoComplete="off"
              />
              {showLocationDropdown && locationSuggestions.length > 0 && (
                <div style={styles.locationDropdown}>
                  {locationSuggestions.map((loc, i) => (
                    <button
                      key={`${loc.city}-${loc.state}-${i}`}
                      type="button"
                      onMouseDown={() => selectLocation(loc)}
                      style={styles.locationOption}
                    >
                      {loc.city}, {loc.state}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {error && <p style={styles.errorText}>{error}</p>}

            <button type="submit" disabled={loading} style={styles.submitBtn}>
              {loading ? 'Please wait…' : 'View Collection →'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ── Filter bottom sheet ───────────────────────────────────────────────────────
function FilterSheet({ meta, activeCategory, activePurity, onApply, onClose }) {
  const [cat, setCat] = useState(activeCategory);
  const [pur, setPur] = useState(activePurity);

  const { categories = [], purities = [] } = meta;

  return (
    <div style={styles.sheetOverlay} onClick={onClose}>
      <div style={styles.sheet} onClick={e => e.stopPropagation()}>
        <div style={styles.sheetHandle} />

        <div style={styles.sheetTitleRow}>
          <span style={styles.sheetTitle}>Filter Collection</span>
          {(cat || pur) && (
            <button style={styles.clearBtn} onClick={() => { setCat(null); setPur(null); }}>
              Clear all
            </button>
          )}
        </div>

        {categories.length > 1 && (
          <>
            <div style={styles.filterLabel}>Category</div>
            <div style={styles.chipRow}>
              <button style={!cat ? styles.chipActive : styles.chip} onClick={() => setCat(null)}>All</button>
              {categories.map(c => (
                <button key={c} style={cat === c ? styles.chipActive : styles.chip} onClick={() => setCat(cat === c ? null : c)}>
                  {c}
                </button>
              ))}
            </div>
          </>
        )}

        {purities.length > 1 && (
          <>
            <div style={styles.filterLabel}>Gold Purity</div>
            <div style={styles.chipRow}>
              <button style={!pur ? styles.chipActive : styles.chip} onClick={() => setPur(null)}>All</button>
              {purities.map(p => (
                <button
                  key={p}
                  style={String(pur) === String(p) ? styles.chipActive : styles.chip}
                  onClick={() => setPur(String(pur) === String(p) ? null : p)}
                >
                  {p}K
                </button>
              ))}
            </div>
          </>
        )}

        {categories.length <= 1 && purities.length <= 1 && (
          <p style={{ color: '#666', fontSize: '14px', textAlign: 'center', marginBottom: '16px' }}>
            No filter options available yet.
          </p>
        )}

        <button style={styles.applyBtn} onClick={() => { onApply(cat, pur); onClose(); }}>
          Apply Filters
        </button>
      </div>
    </div>
  );
}

// ── Single item card ──────────────────────────────────────────────────────────
function ItemCard({ item, index, total, onFilterClick, filterActive }) {
  const [imgError, setImgError] = useState(false);

  // Compute total stone weight in grams (convert carats, skip count-based units)
  const stoneWeightGrams = item.stones && item.stones.length > 0
    ? item.stones.reduce((sum, s) => {
      const w = s.total_weight || 0;
      if (s.unit === 'ct') return sum + w * 0.2;
      if (s.unit === 'nos') return sum;
      return sum + w;
    }, 0)
    : (parseFloat(item.stone_weight) || 0);

  return (
    <div style={styles.card}>
      {/* Image — top half */}
      <div style={styles.imageContainer}>
        {item.image_url && !imgError ? (
          <img
            src={item.image_url}
            alt={item.name || item.code}
            style={styles.image}
            onError={() => setImgError(true)}
          />
        ) : (
          <div style={styles.imagePlaceholder}>
            <span style={styles.placeholderCode}>{item.code}</span>
            <span style={styles.placeholderLabel}>No Image</span>
          </div>
        )}
        {/* Filter button — top left */}
        <button
          style={{ ...styles.filterBtn, ...(filterActive ? styles.filterBtnActive : {}) }}
          onClick={e => { e.stopPropagation(); onFilterClick(); }}
        >
          {filterActive ? `≡ Filtered` : '≡ Filter'}
        </button>
        {/* Counter badge — top right */}
        <div style={styles.counterBadge}>{index + 1} / {total}</div>
      </div>

      {/* Details — bottom half */}
      <div style={styles.detailsPanel}>
        {/* Item header row */}
        <div style={styles.itemHeader}>
          <span style={styles.itemTag}>{item.code}</span>
          {item.est_price_usd > 0 && (
            <span style={styles.price}>
              Est Price : ${item.est_price_usd.toLocaleString('en-US')}
            </span>
          )}
        </div>

        {/* Weight row */}
        <div style={styles.weightRow}>
          <div style={styles.weightItem}>
            <span style={styles.weightLabel}>Gross Wt</span>
            <span style={styles.weightValue}>{formatWeight(item.gross_weight)}</span>
          </div>
          <div style={styles.weightDivider} />
          <div style={styles.weightItem}>
            <span style={styles.weightLabel}>Net Wt</span>
            <span style={styles.weightValue}>{formatWeight(item.net_weight)}</span>
          </div>
          <div style={styles.weightDivider} />
          <div style={styles.weightItem}>
            <span style={styles.weightLabel}>Stone Wt</span>
            <span style={styles.weightValue}>{formatWeight(stoneWeightGrams)}</span>
          </div>
          {item.gold_purity && (
            <>
              <div style={styles.weightDivider} />
              <div style={styles.weightItem}>
                <span style={styles.weightLabel}>Purity</span>
                <span style={styles.weightValue}>{item.gold_purity}K</span>
              </div>
            </>
          )}
        </div>

        {/* Stones table */}
        {item.stones && item.stones.length > 0 && (
          <div style={styles.stonesSection}>
            <div style={styles.stonesHeader}>
              <span style={styles.stonesCol}>Code</span>
              <span style={styles.stonesColRight}>Weight</span>
              <span style={styles.stonesColRight}>Value</span>
            </div>
            {item.stones.map((stone, i) => (
              <div key={i} style={styles.stoneRow}>
                <span style={styles.stoneCode}>{stone.code}</span>
                <span style={styles.stoneWeight}>
                  {stone.count > 1 ? `${stone.count} / ` : ''}
                  {stone.total_weight.toFixed(2)} {stone.unit}
                </span>
                <span style={styles.stoneCost}>
                  {stone.sale_price > 0 ? formatCurrency(Math.round(stone.total_weight * stone.sale_price)) : '—'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main catalog viewer ───────────────────────────────────────────────────────
function CatalogViewer({ items, total, loading, loadingMore, meta, onFilterChange, onLoadMore }) {
  const [index, setIndex] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [activePurity, setActivePurity] = useState(null);
  const touchStartX = useRef(null);

  // Item code search
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchCode, setSearchCode] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [searchResult, setSearchResult] = useState(null); // single item overlay

  const filterActive = !!(activeCategory || activePurity);
  const hasMore = items.length < total;

  const handleCodeSearch = async (e) => {
    e.preventDefault();
    const code = searchCode.trim().toUpperCase();
    if (!code) return;
    setSearchLoading(true);
    setSearchError('');
    setSearchResult(null);
    try {
      const res = await apiFetch(`/api/public/catalog/item/${encodeURIComponent(code)}`);
      if (res.status === 404) {
        setSearchError(`Item "${code}" not found or not in stock.`);
      } else if (!res.ok) {
        setSearchError('Failed to fetch item. Please try again.');
      } else {
        const data = await res.json();
        setSearchResult(data.item);
      }
    } catch {
      setSearchError('Network error. Please try again.');
    } finally {
      setSearchLoading(false);
    }
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchCode('');
    setSearchError('');
    setSearchResult(null);
  };

  const goNext = useCallback(() => {
    setIndex(i => Math.min(i + 1, items.length - 1));
  }, [items.length]);

  const goPrev = useCallback(() => {
    setIndex(i => Math.max(i - 1, 0));
  }, []);

  // Auto-load next page when customer is within 5 items of the end
  useEffect(() => {
    if (hasMore && !loadingMore && index >= items.length - 5) {
      onLoadMore(activeCategory, activePurity);
    }
  }, [index, items.length, hasMore, loadingMore, activeCategory, activePurity, onLoadMore]);

  const handleFilterApply = useCallback((cat, pur) => {
    setActiveCategory(cat);
    setActivePurity(pur);
    setIndex(0);
    onFilterChange(cat, pur);
  }, [onFilterChange]);

  const handleClearFilters = useCallback(() => {
    setActiveCategory(null);
    setActivePurity(null);
    setIndex(0);
    onFilterChange(null, null);
  }, [onFilterChange]);

  const handleTouchStart = (e) => {
    if (filterOpen) return;
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (filterOpen || touchStartX.current === null) return;
    const dx = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(dx) > 40) {
      if (dx > 0) goNext(); else goPrev();
    }
    touchStartX.current = null;
  };

  if (loading) {
    return (
      <div style={styles.loadingScreen}>
        <div style={styles.spinner} />
        <p style={styles.loadingText}>Loading collection…</p>
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div style={styles.viewer}>
        <div style={styles.noResults}>
          <div style={styles.noResultsIcon}>🔍</div>
          <p style={styles.noResultsText}>No items match your filters</p>
          <button style={styles.clearFiltersBtn} onClick={handleClearFilters}>
            Clear Filters
          </button>
          <button style={styles.noResultsFilterBtn} onClick={() => setFilterOpen(true)}>
            Adjust Filters
          </button>
        </div>
        {filterOpen && (
          <FilterSheet
            meta={meta}
            activeCategory={activeCategory}
            activePurity={activePurity}
            onApply={handleFilterApply}
            onClose={() => setFilterOpen(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div
      style={styles.viewer}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <ItemCard
        item={items[index]}
        index={index}
        total={total}
        onFilterClick={() => setFilterOpen(true)}
        filterActive={filterActive}
      />

      {/* Dot navigation — reflects loaded items, fades out when loading more */}
      {items.length > 1 && (
        <div style={{ ...styles.dots, opacity: loadingMore ? 0.5 : 1 }}>
          {items.slice(Math.max(0, index - 4), Math.min(items.length, index + 5)).map((_, dotOffset) => {
            const actualIndex = Math.max(0, index - 4) + dotOffset;
            return (
              <button
                key={actualIndex}
                onClick={() => setIndex(actualIndex)}
                style={{ ...styles.dot, ...(actualIndex === index ? styles.dotActive : {}) }}
                aria-label={`Go to item ${actualIndex + 1}`}
              />
            );
          })}
          {hasMore && <span style={styles.moreIndicator}>…</span>}
        </div>
      )}

      {index === 0 && items.length > 1 && (
        <p style={styles.swipeHint}>← Swipe to browse →</p>
      )}

      {/* Item Code Search Button */}
      <button
        style={styles.searchCodeBtn}
        onClick={() => setSearchOpen(true)}
        aria-label="Search by item code"
      >
        🔍 Search by Code
      </button>

      {filterOpen && (
        <FilterSheet
          meta={meta}
          activeCategory={activeCategory}
          activePurity={activePurity}
          onApply={handleFilterApply}
          onClose={() => setFilterOpen(false)}
        />
      )}

      {/* Item Code Search Overlay */}
      {searchOpen && (
        <div style={styles.sheetOverlay} onClick={closeSearch}>
          <div style={styles.sheet} onClick={e => e.stopPropagation()}>
            <div style={styles.sheetHandle} />
            <div style={styles.sheetTitleRow}>
              <span style={styles.sheetTitle}>Search by Item Code</span>
              <button style={styles.clearBtn} onClick={closeSearch}>✕ Close</button>
            </div>

            <form onSubmit={handleCodeSearch} style={{ marginBottom: '16px' }}>
              <div style={styles.searchInputRow}>
                <input
                  type="text"
                  value={searchCode}
                  onChange={e => { setSearchCode(e.target.value.toUpperCase()); setSearchError(''); setSearchResult(null); }}
                  placeholder="e.g. GDS-001"
                  style={styles.searchInput}
                  autoFocus
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                />
                <button
                  type="submit"
                  disabled={searchLoading || !searchCode.trim()}
                  style={styles.searchSubmitBtn}
                >
                  {searchLoading ? '…' : 'Go'}
                </button>
              </div>
            </form>

            {searchError && (
              <p style={{ color: '#e53e3e', fontSize: '14px', textAlign: 'center', marginBottom: '12px' }}>
                {searchError}
              </p>
            )}

            {searchResult && (
              <div style={styles.searchResultCard}>
                {searchResult.image_url ? (
                  <img
                    src={searchResult.image_url}
                    alt={searchResult.code}
                    style={styles.searchResultImg}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div style={styles.searchResultImgPlaceholder}>
                    <span style={{ color: GOLD, fontSize: '20px', fontWeight: '700' }}>{searchResult.code}</span>
                  </div>
                )}
                <div style={styles.searchResultInfo}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={styles.itemTag}>{searchResult.code}</span>
                    {searchResult.category && (
                      <span style={styles.categoryBadge}>{searchResult.category}</span>
                    )}
                  </div>
                  {searchResult.gold_purity && (
                    <span style={{ fontSize: '12px', color: '#888' }}>{searchResult.gold_purity}K Gold</span>
                  )}
                  <div style={{ marginTop: '6px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    {searchResult.gross_weight > 0 && (
                      <span style={{ fontSize: '12px', color: '#555' }}>
                        Gross: <strong>{formatWeight(searchResult.gross_weight)}</strong>
                      </span>
                    )}
                    {searchResult.net_weight > 0 && (
                      <span style={{ fontSize: '12px', color: '#555' }}>
                        Net: <strong>{formatWeight(searchResult.net_weight)}</strong>
                      </span>
                    )}
                    {searchResult.stone_weight > 0 && (
                      <span style={{ fontSize: '12px', color: '#555' }}>
                        Stone: <strong>{formatWeight(searchResult.stone_weight)}</strong>
                      </span>
                    )}
                  </div>
                  {searchResult.est_price_usd > 0 && (
                    <div style={{ marginTop: '6px', fontSize: '16px', fontWeight: '700', color: '#1a5c00' }}>
                      Est Price : ${searchResult.est_price_usd.toLocaleString('en-US')}
                    </div>
                  )}
                  {searchResult.stones && searchResult.stones.length > 0 && (
                    <div style={{ marginTop: '8px' }}>
                      <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Stones</div>
                      {searchResult.stones.map(s => (
                        <div key={s.code} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#555', padding: '2px 0' }}>
                          <span>{s.name || s.code} ({s.count})</span>
                          <span>{s.total_weight.toFixed(2)} {s.unit}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const PAGE_SIZE = 30;

// ── Root component ────────────────────────────────────────────────────────────
export default function CustomerCatalog() {
  // null = checking, 'ok' = access granted, 'disabled' | 'expired' | 'invalid_token' = blocked
  const [tokenValid, setTokenValid] = useState(null);
  const [sessionValid, setSessionValid] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [catalogMeta, setCatalogMeta] = useState({ categories: [], purities: [] });

  useEffect(() => {
    const handleBlockedAccess = event => {
      setSessionValid(false);
      setTokenValid(event.detail === 'expired' ? 'expired' : 'disabled');
    };
    window.addEventListener('catalog-access-blocked', handleBlockedAccess);
    return () => window.removeEventListener('catalog-access-blocked', handleBlockedAccess);
  }, []);

  // Verify QR access token from URL (?t=...) — must be present and valid
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('t');
    if (!t) {
      apiFetch('/api/public/catalog/settings')
        .then(r => r.json())
        .then(data => {
          const expired = data.catalog_expires_at && new Date(data.catalog_expires_at) < new Date();
          setTokenValid(data.catalog_enabled === false ? 'disabled' : (expired ? 'expired' : 'invalid_token'));
        })
        .catch(() => setTokenValid('invalid_token'));
      return;
    }
    apiFetch(`/api/public/catalog/verify-token?t=${encodeURIComponent(t)}`)
      .then(r => r.json())
      .then(data => {
        if (data.valid === true) {
          setTokenValid('ok');
          window.history.replaceState(null, '', '/catalog');
        } else {
          setTokenValid(data.reason || 'invalid_token');
        }
      })
      .catch(() => setTokenValid('invalid_token'));
  }, []);

  // Check for existing non-expired session in localStorage
  useEffect(() => {
    if (tokenValid !== 'ok') return;
    try {
      const stored = JSON.parse(localStorage.getItem('catalog_session') || 'null');
      if (stored && new Date(stored.expires_at) > new Date()) {
        setSessionValid(true);
      }
    } catch {
      localStorage.removeItem('catalog_session');
    }
  }, [tokenValid]);

  // Core fetch — page 1 replaces items, subsequent pages append
  const fetchItems = useCallback(async ({ category = null, purity = null, pageNum = 1, append = false } = {}) => {
    if (append) setLoadingMore(true); else setCatalogLoading(true);
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE, page: pageNum });
      if (category) params.set('category', category);
      if (purity) params.set('purity', purity);
      const data = await apiFetch(`/api/public/catalog?${params}`).then(r => r.json());
      setItems(prev => append ? [...prev, ...(data.items || [])] : (data.items || []));
      setTotal(data.total || 0);
      setPage(pageNum);
    } catch {
      if (!append) setItems([]);
    } finally {
      if (append) setLoadingMore(false); else setCatalogLoading(false);
    }
  }, []);

  // Fetch meta + first page when session is established
  useEffect(() => {
    if (!sessionValid) return;
    apiFetch('/api/public/catalog/meta')
      .then(r => r.json())
      .then(data => setCatalogMeta(data))
      .catch(() => { });
    fetchItems({ pageNum: 1 });
  }, [sessionValid, fetchItems]);

  // Called by CatalogViewer when a filter is applied
  const handleFilterChange = useCallback((category, purity) => {
    fetchItems({ category, purity, pageNum: 1, append: false });
  }, [fetchItems]);

  // Called by CatalogViewer when the customer nears the last item
  const handleLoadMore = useCallback((category, purity) => {
    fetchItems({ category, purity, pageNum: page + 1, append: true });
  }, [fetchItems, page]);

  // Customer submits name + phone → create session directly (OTP skipped until messaging is integrated)
  const handleCreateSession = async ({ customer_name, customer_phone, customer_city, customer_state }) => {
    setSessionLoading(true);
    try {
      const res = await apiFetch('/api/public/catalog/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_name, customer_phone, customer_city, customer_state })
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.reason === 'disabled' || data.reason === 'expired') {
          setTokenValid(data.reason);
        }
        return;
      }

      localStorage.setItem('catalog_session', JSON.stringify({
        session_token: data.session_token,
        expires_at: data.expires_at,
        customer_name,
        customer_phone
      }));
      setSessionValid(true);
    } catch {
      setSessionValid(false);
    } finally {
      setSessionLoading(false);
    }
  };

  if (tokenValid === null) {
    return (
      <div style={styles.loadingScreen}>
        <div style={styles.spinner} />
        <p style={styles.loadingText}>Verifying access…</p>
      </div>
    );
  }

  if (tokenValid === 'disabled' || tokenValid === 'expired') {
    return (
      <div style={styles.blockScreen}>
        <div style={styles.blockCard}>
          <div style={styles.blockIcon}>{tokenValid === 'expired' ? '⏰' : '🚫'}</div>
          <div style={styles.blockTitle}>Catalog Unavailable</div>
          <div style={styles.blockBody}>
            {tokenValid === 'expired'
              ? 'The catalog access period has ended. Please visit us at our store to browse the collection.'
              : 'The catalog is currently not available. Please check with our staff for more details.'}
          </div>
        </div>
      </div>
    );
  }

  if (tokenValid === 'invalid_token') {
    return (
      <div style={styles.blockScreen}>
        <div style={styles.blockCard}>
          <div style={styles.blockIcon}>🔒</div>
          <div style={styles.blockTitle}>QR Code Required</div>
          <div style={styles.blockBody}>
            Please scan the QR code at our counter to browse the collection.
          </div>
        </div>
      </div>
    );
  }

  if (!sessionValid) {
    return <CustomerInfoForm onSubmit={handleCreateSession} loading={sessionLoading} />;
  }

  return (
    <CatalogViewer
      items={items}
      total={total}
      loading={catalogLoading}
      loadingMore={loadingMore}
      meta={catalogMeta}
      onFilterChange={handleFilterChange}
      onLoadMore={handleLoadMore}
    />
  );
}

// ── Styles (inline, no external CSS dependency) ───────────────────────────────
const GOLD = '#B8860B';
const DARK = '#1a1a1a';
const PAGE_BG = '#F2EDE4';
const PANEL_BG = '#FFFCF7';
const BORDER = '#D8CFBF';
const MUTED = '#6F675C';

const styles = {
  // Form screen
  formScreen: {
    minHeight: '100dvh',
    background: 'linear-gradient(160deg, #1a1a1a 0%, #2d2d2d 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    fontFamily: "'Segoe UI', system-ui, sans-serif"
  },
  formCard: {
    background: '#fff',
    borderRadius: '20px',
    width: '100%',
    maxWidth: '380px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
  },
  brandHeader: {
    background: `linear-gradient(135deg, ${DARK} 0%, #333 100%)`,
    padding: '28px 24px 24px',
    textAlign: 'center',
    borderBottom: `3px solid ${GOLD}`
  },
  brandTitle: {
    color: GOLD,
    fontSize: '26px',
    fontWeight: '700',
    letterSpacing: '2px',
    textTransform: 'uppercase'
  },
  brandSubtitle: {
    color: '#aaa',
    fontSize: '12px',
    marginTop: '4px',
    letterSpacing: '1px'
  },
  formBody: { padding: '28px 24px' },
  formIntro: {
    color: '#555',
    fontSize: '14px',
    marginBottom: '20px',
    lineHeight: '1.5',
    textAlign: 'center'
  },
  fieldGroup: { marginBottom: '16px' },
  label: {
    display: 'block',
    fontSize: '12px',
    fontWeight: '600',
    color: '#444',
    marginBottom: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    border: '1.5px solid #ddd',
    borderRadius: '10px',
    fontSize: '16px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
    color: DARK
  },
  locationDropdown: {
    position: 'absolute',
    zIndex: 9999,
    marginTop: '4px',
    width: '100%',
    background: '#fff',
    border: '1.5px solid #ddd',
    borderRadius: '10px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
    overflow: 'hidden'
  },
  locationOption: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '10px 14px',
    fontSize: '14px',
    color: DARK,
    background: 'none',
    border: 'none',
    borderBottom: '1px solid #eee',
    cursor: 'pointer'
  },
  errorText: {
    color: '#e53e3e',
    fontSize: '13px',
    marginBottom: '12px',
    textAlign: 'center'
  },
  submitBtn: {
    width: '100%',
    padding: '14px',
    background: `linear-gradient(135deg, ${GOLD} 0%, #a07400 100%)`,
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    marginTop: '8px',
    letterSpacing: '0.5px'
  },
  // Loading / empty
  loadingScreen: {
    height: '100dvh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: PAGE_BG,
    fontFamily: "'Segoe UI', system-ui, sans-serif"
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: `3px solid rgba(184,134,11,0.3)`,
    borderTop: `3px solid ${GOLD}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  loadingText: { color: MUTED, marginTop: '16px', fontSize: '14px', fontWeight: '600' },
  emptyScreen: {
    height: '100dvh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: PAGE_BG,
    padding: '24px',
    fontFamily: "'Segoe UI', system-ui, sans-serif"
  },
  emptyText: { color: GOLD, fontSize: '18px', fontWeight: '600', textAlign: 'center' },
  emptySubtext: { color: MUTED, fontSize: '14px', marginTop: '8px', textAlign: 'center' },

  // Viewer
  viewer: {
    height: '100dvh',
    display: 'flex',
    flexDirection: 'column',
    background: PAGE_BG,
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    userSelect: 'none',
    overflow: 'hidden'
  },

  // Card
  card: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },

  // Image section
  imageContainer: {
    flex: '0 0 52%',
    position: 'relative',
    overflow: 'hidden',
    background: '#E7DFD2',
    borderBottom: `1px solid ${BORDER}`
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center'
  },
  imagePlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(145deg, #E8E0D3 0%, #DCD1C0 100%)'
  },
  placeholderCode: {
    color: GOLD,
    fontSize: '28px',
    fontWeight: '700',
    letterSpacing: '2px'
  },
  placeholderLabel: { color: MUTED, fontSize: '12px', marginTop: '8px', fontWeight: '600' },
  counterBadge: {
    position: 'absolute',
    top: '10px',
    right: '12px',
    background: 'rgba(255,252,247,0.94)',
    color: DARK,
    fontSize: '12px',
    minHeight: '36px',
    padding: '0 12px',
    borderRadius: '20px',
    border: `1px solid ${BORDER}`,
    boxShadow: '0 2px 10px rgba(45,35,20,0.16)',
    display: 'flex',
    alignItems: 'center',
    backdropFilter: 'blur(8px)'
  },

  // Details panel
  detailsPanel: {
    flex: '0 0 48%',
    background: PANEL_BG,
    padding: '10px 14px 6px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    overflow: 'hidden',
    color: DARK
  },
  itemHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px'
  },
  itemTag: {
    fontSize: '20px',
    fontWeight: '700',
    color: DARK,
    letterSpacing: '1px'
  },
  categoryBadge: {
    marginLeft: '8px',
    fontSize: '11px',
    background: '#f0e8c8',
    color: '#7a5c00',
    padding: '2px 8px',
    borderRadius: '10px',
    fontWeight: '600'
  },
  price: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1a5c00'
  },

  // Weight row
  weightRow: {
    display: 'flex',
    alignItems: 'center',
    background: '#fff',
    borderRadius: '8px',
    padding: '6px 10px',
    border: '1px solid #e8e4d8'
  },
  weightItem: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  weightDivider: {
    width: '1px',
    height: '28px',
    background: '#e0dcd0',
    margin: '0 4px'
  },
  weightLabel: {
    fontSize: '10px',
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    fontWeight: '600'
  },
  weightValue: {
    fontSize: '14px',
    fontWeight: '700',
    color: DARK,
    marginTop: '2px'
  },

  // Stones
  stonesSection: {
    background: '#fff',
    borderRadius: '8px',
    border: '1px solid #e8e4d8',
    overflow: 'hidden',
    flex: 1
  },
  stonesHeader: {
    display: 'flex',
    padding: '4px 10px',
    background: `linear-gradient(90deg, ${DARK} 0%, #333 100%)`,
    color: GOLD,
    fontSize: '11px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  stonesCol: { flex: '0 0 30%' },
  stonesColRight: { flex: 1, textAlign: 'right' },
  stoneRow: {
    display: 'flex',
    padding: '5px 10px',
    borderBottom: '1px solid #f0ece0',
    fontSize: '14px',
    alignItems: 'center'
  },
  stoneCode: {
    flex: '0 0 30%',
    fontWeight: '700',
    color: DARK
  },
  stoneWeight: {
    flex: 1,
    textAlign: 'right',
    color: '#555'
  },
  stoneCost: {
    flex: 1,
    textAlign: 'right',
    fontWeight: '600',
    color: '#1a5c00'
  },

  // Dots navigation
  dots: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '6px 0 4px',
    gap: '6px',
    background: PAGE_BG
  },
  dot: {
    width: '9px',
    height: '9px',
    borderRadius: '50%',
    background: '#B9AE9D',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    transition: 'all 0.2s'
  },
  dotActive: {
    background: GOLD,
    width: '18px',
    borderRadius: '4px'
  },
  moreIndicator: {
    color: MUTED,
    fontSize: '14px',
    lineHeight: '7px',
    paddingBottom: '2px',
  },
  swipeHint: {
    textAlign: 'center',
    color: MUTED,
    fontSize: '12px',
    padding: '0 0 2px',
    background: PAGE_BG,
    margin: 0
  },

  // Item code search button (bottom bar)
  searchCodeBtn: {
    display: 'block',
    width: '100%',
    minHeight: '44px',
    padding: '10px 16px',
    background: PANEL_BG,
    color: '#765700',
    border: 'none',
    borderTop: `1px solid ${BORDER}`,
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    letterSpacing: '0.3px',
    textAlign: 'center',
  },

  // Search overlay input row
  searchInputRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '8px',
  },
  searchInput: {
    flex: 1,
    padding: '12px 14px',
    border: '1.5px solid #ddd',
    borderRadius: '10px',
    fontSize: '16px',
    outline: 'none',
    boxSizing: 'border-box',
    color: DARK,
    fontWeight: '600',
    letterSpacing: '1px',
    textTransform: 'uppercase',
  },
  searchSubmitBtn: {
    padding: '12px 20px',
    background: GOLD,
    color: '#000',
    border: 'none',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
  },

  // Search result card
  searchResultCard: {
    display: 'flex',
    gap: '12px',
    background: '#f9f7f2',
    borderRadius: '12px',
    border: '1px solid #e8e4d8',
    overflow: 'hidden',
    padding: '0',
  },
  searchResultImg: {
    width: '100px',
    height: '100px',
    objectFit: 'cover',
    flexShrink: 0,
    borderRadius: '12px 0 0 12px',
  },
  searchResultImgPlaceholder: {
    width: '100px',
    height: '100px',
    background: '#222',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    borderRadius: '12px 0 0 12px',
  },
  searchResultInfo: {
    flex: 1,
    padding: '10px 12px 10px 0',
    overflow: 'hidden',
  },


  filterBtn: {
    position: 'absolute',
    top: '10px',
    left: '12px',
    background: 'rgba(255,252,247,0.94)',
    color: DARK,
    fontSize: '12px',
    minHeight: '36px',
    padding: '0 12px',
    borderRadius: '20px',
    backdropFilter: 'blur(8px)',
    border: `1px solid ${BORDER}`,
    boxShadow: '0 2px 10px rgba(45,35,20,0.16)',
    cursor: 'pointer',
  },
  filterBtnActive: {
    background: `rgba(184,134,11,0.9)`,
    color: '#fff',
  },

  // No-results state
  noResults: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: PAGE_BG,
    padding: '32px 24px',
    gap: '12px',
  },
  noResultsIcon: { fontSize: '48px' },
  noResultsText: {
    color: DARK,
    fontSize: '16px',
    fontWeight: '600',
    textAlign: 'center',
    margin: 0,
  },
  clearFiltersBtn: {
    padding: '10px 24px',
    background: GOLD,
    color: '#000',
    border: 'none',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  noResultsFilterBtn: {
    padding: '8px 20px',
    background: 'transparent',
    color: DARK,
    border: `1px solid ${BORDER}`,
    borderRadius: '20px',
    fontSize: '13px',
    cursor: 'pointer',
  },

  // Filter bottom sheet
  sheetOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.6)',
    zIndex: 100,
    display: 'flex',
    alignItems: 'flex-end',
  },
  sheet: {
    width: '100%',
    background: PANEL_BG,
    borderRadius: '20px 20px 0 0',
    padding: '12px 20px 36px',
    boxSizing: 'border-box',
    boxShadow: '0 -12px 40px rgba(45,35,20,0.2)',
    maxHeight: '88dvh',
    overflowY: 'auto',
  },
  sheetHandle: {
    width: '40px',
    height: '4px',
    background: '#B9AE9D',
    borderRadius: '2px',
    margin: '0 auto 16px',
  },
  sheetTitleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  sheetTitle: {
    color: DARK,
    fontSize: '16px',
    fontWeight: '700',
    letterSpacing: '0.5px',
  },
  clearBtn: {
    color: '#765700',
    fontSize: '13px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    textDecoration: 'underline',
    padding: 0,
  },
  filterLabel: {
    color: MUTED,
    fontSize: '11px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: '10px',
  },
  chipRow: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    marginBottom: '20px',
  },
  chip: {
    padding: '7px 16px',
    borderRadius: '20px',
    border: `1.5px solid ${BORDER}`,
    background: '#FFFFFF',
    color: DARK,
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  chipActive: {
    padding: '7px 16px',
    borderRadius: '20px',
    border: `1.5px solid ${GOLD}`,
    background: GOLD,
    color: '#000',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  applyBtn: {
    width: '100%',
    padding: '14px',
    background: `linear-gradient(135deg, ${GOLD} 0%, #a07400 100%)`,
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    marginTop: '4px',
    letterSpacing: '0.5px',
    boxSizing: 'border-box',
  },

  // Block screen (invalid / missing QR token)
  blockScreen: {
    height: '100dvh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(160deg, #1a1a1a 0%, #2d2d2d 100%)',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    padding: '24px'
  },
  blockCard: {
    textAlign: 'center',
    maxWidth: '300px'
  },
  blockIcon: {
    fontSize: '56px',
    marginBottom: '16px'
  },
  blockTitle: {
    color: GOLD,
    fontSize: '22px',
    fontWeight: '700',
    letterSpacing: '1px',
    marginBottom: '12px'
  },
  blockBody: {
    color: '#888',
    fontSize: '15px',
    lineHeight: '1.6'
  }
};
